import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Megaphone, ClipboardList, X, ChevronDown, ChevronUp } from "lucide-react";
import DateField from "@/components/app/DateField";

function RsvpPanel({ ann, players }) {
  const [open, setOpen] = useState(false);
  const [rsvps, setRsvps] = useState(null);
  const toggle = async () => {
    if (!open && rsvps === null) {
      try { const { data } = await api.get(`/rsvps/${ann.id}`); setRsvps(data || []); }
      catch { setRsvps([]); }
    }
    setOpen((v) => !v);
  };
  const targeted = ann.scope === "players" && ann.player_ids?.length
    ? players.filter((p) => ann.player_ids.includes(p.id))
    : players.filter((p) => p.team_id === ann.target_id);
  const byId = Object.fromEntries((rsvps || []).map((r) => [r.player_id, r.status]));
  const yes = targeted.filter((p) => byId[p.id] === "yes");
  const no = targeted.filter((p) => byId[p.id] === "no");
  const pending = targeted.filter((p) => !byId[p.id]);
  const nameOf = (pid) => players.find((p) => p.id === pid)?.name || "Jugador";
  const extraYes = (rsvps || []).filter((r) => r.status === "yes" && !targeted.some((p) => p.id === r.player_id));
  const extraNo = (rsvps || []).filter((r) => r.status === "no" && !targeted.some((p) => p.id === r.player_id));
  const Col = ({ icon, label, color, names, testid }) => (
    <div className="flex-1 min-w-[140px]" data-testid={testid}>
      <div className={`text-[10px] uppercase font-heading font-black tracking-wider ${color}`}>{icon} {label} ({names.length})</div>
      <div className="mt-1 space-y-0.5">
        {names.length === 0 ? <div className="text-[11px] text-muted-foreground">—</div>
          : names.map((n) => <div key={n} className="text-[11px] font-mono truncate">{n}</div>)}
      </div>
    </div>
  );
  return (
    <div className="mt-3">
      <button data-testid={`rsvp-toggle-${ann.id}`} onClick={toggle}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase font-heading font-black tracking-wider text-primary hover:underline">
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Respuestas de las familias
      </button>
      {open && rsvps !== null && (
        <div className="mt-2 border border-border p-3 flex flex-wrap gap-4" data-testid={`rsvp-panel-${ann.id}`}>
          <Col icon="✅" label="Confirmados" color="text-emerald-500" testid={`rsvp-yes-${ann.id}`}
               names={[...yes.map((p) => p.name), ...extraYes.map((r) => nameOf(r.player_id))]} />
          <Col icon="❌" label="No asisten" color="text-red-500" testid={`rsvp-no-${ann.id}`}
               names={[...no.map((p) => p.name), ...extraNo.map((r) => nameOf(r.player_id))]} />
          <Col icon="⏳" label="Sin responder" color="text-amber-400" testid={`rsvp-pending-${ann.id}`}
               names={pending.map((p) => p.name)} />
        </div>
      )}
    </div>
  );
}

export default function AnnouncementsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [teams, setTeams] = useState([]);
  const [cats, setCats] = useState([]);
  const [users, setUsers] = useState([]);
  const [show, setShow] = useState(false);
  const defaultScope = ["coach", "delegado"].includes(user?.role) ? "team" : "club";
  const [form, setForm] = useState({ title: "", body: "", scope: defaultScope, target_id: "", target_ids: [], player_ids: [], kind: "announcement", date: "" });
  const [players, setPlayers] = useState([]);
  const [playerFilterTeam, setPlayerFilterTeam] = useState("");

  const load = async () => {
    try {
      const [a, t, c] = await Promise.all([
        api.get("/announcements"),
        api.get("/teams"),
        api.get("/categories").catch(() => ({ data: [] })),
      ]);
      setItems(a.data);
      setTeams(t.data);
      setCats(c.data);
      // Load players — anyone who can post announcements can also target players in their scope.
      if (["club_admin", "coordinator", "director_deportivo", "coach", "delegado"].includes(user?.role)) {
        try { const p = await api.get("/players"); setPlayers(p.data); } catch { /* optional */ }
      }
      // Only admin-level roles can send to staff/custom → load users list.
      if (["club_admin", "coordinator", "director_deportivo"].includes(user?.role)) {
        try { const u = await api.get("/users"); setUsers(u.data); } catch { /* optional */ }
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.target_id) payload.target_id = null;
      if (!payload.date) payload.date = null;
      if (payload.scope !== "custom") payload.target_ids = null;
      if (payload.scope !== "players") payload.player_ids = null;
      await api.post("/announcements", payload);
      toast.success("Publicado");
      setShow(false);
      setForm({ title: "", body: "", scope: defaultScope, target_id: "", target_ids: [], player_ids: [], kind: "announcement", date: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar publicación?")) return;
    try {
      await api.delete(`/announcements/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const targetLabel = (a) => {
    if (a.scope === "club") return "Todo el club";
    if (a.scope === "staff") return "Staff técnico";
    if (a.scope === "team") return teams.find(t => t.id === a.target_id)?.name || "Equipo";
    if (a.scope === "category") return cats.find(c => c.id === a.target_id)?.name || "Categoría";
    if (a.scope === "custom") return `${(a.target_ids || []).length} usuarios`;
    if (a.scope === "players") return `${(a.player_ids || []).length} jugador(es)/tutores`;
    return "—";
  };

  const canPost = ["club_admin", "coordinator", "director_deportivo", "coach", "delegado"].includes(user?.role);
  const canPostStaffOrCustom = ["club_admin", "coordinator", "director_deportivo"].includes(user?.role);
  const isTeamStaff = ["coach", "delegado"].includes(user?.role);
  // Coaches/delegados only see their assigned teams for the "team" scope selector.
  // Backend `/teams` already returns only the user's assigned teams for these roles.
  const visibleTeams = teams;
  // Category options: coaches/delegados aren't allowed to post by category → hide it entirely.
  // For admins we show every category. For coaches the option is disabled below.

  return (
    <div>
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="overline">Portal de comunicación</div>
          <h1 className="font-heading font-extrabold text-5xl tracking-tighter mt-2">Comunicaciones del club</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-lg">Canal oficial para convocatorias, horarios y anuncios. Sustituye al grupo de WhatsApp con trazabilidad y control.</p>
        </div>
        {canPost && (
          <button data-testid="new-ann-btn" onClick={() => setShow(true)} className="btn-volt"><Plus size={16} /> Nueva publicación</button>
        )}
      </div>

      <div className="space-y-3">
        {items.map((a) => (
          <div key={a.id} data-testid={`ann-item-${a.id}`} className="border border-border p-5 hover:border-primary/40 transition-colors">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-10 h-10 border border-border flex items-center justify-center shrink-0">
                  {a.kind === "convocatoria" ? <ClipboardList size={16} className="text-primary" /> : <Megaphone size={16} className="text-primary" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="overline">{a.kind === "convocatoria" ? "Convocatoria" : "Anuncio"}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">· {targetLabel(a)}</span>
                    {a.date && <span className="text-[10px] font-mono text-primary">· {a.date}</span>}
                  </div>
                  <div className="font-heading font-bold text-lg uppercase tracking-tight">{a.title}</div>
                  <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{a.body}</p>
                  {a.kind === "convocatoria" && <RsvpPanel ann={a} players={players} />}
                  <div className="text-[10px] font-mono text-muted-foreground mt-3">
                    Por {a.author_name} · {a.created_at.slice(0, 16).replace("T", " ")}
                  </div>
                </div>
              </div>
              {(user?.role !== "coach" && user?.role !== "delegado" || a.author_id === user?.id) && (
                <button data-testid={`del-ann-${a.id}`} onClick={() => remove(a.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-center py-16 border border-dashed border-border">
            <Megaphone className="mx-auto text-muted-foreground" size={28} />
            <div className="mt-3 text-muted-foreground text-sm">Aún no hay publicaciones.</div>
          </div>
        )}
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={submit} className="bg-card border border-border w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">Nueva</div>
                <div className="font-heading font-black text-2xl uppercase tracking-tight">Publicar</div>
              </div>
              <button type="button" onClick={() => setShow(false)} className="p-1 hover:text-primary"><X size={18} /></button>
            </div>
            <div>
              <label className="overline block mb-1">Tipo</label>
              <select data-testid="ann-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
                <option value="announcement">Anuncio general</option>
                <option value="convocatoria">Convocatoria / Horario</option>
              </select>
            </div>
            <div>
              <label className="overline block mb-1">Título *</label>
              <input required data-testid="ann-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="overline block mb-1">Mensaje *</label>
              <textarea required data-testid="ann-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={5} className="w-full bg-transparent border border-border p-3 font-mono text-sm focus:border-primary focus:outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-1">Ámbito</label>
                <select data-testid="ann-scope" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value, target_id: "", target_ids: [], player_ids: [] })} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
                  {!isTeamStaff && <option value="club">Todo el club</option>}
                  {!isTeamStaff && <option value="category">Categoría</option>}
                  <option value="team">Equipo</option>
                  <option value="players">Jugadores / tutores concretos</option>
                  <option value="staff">Staff técnico</option>
                  {canPostStaffOrCustom && <option value="custom">Usuarios concretos</option>}
                </select>
              </div>
              <div>
                <label className="overline block mb-1">Fecha (opcional)</label>
                <DateField value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="ann-date" placeholder="Selecciona fecha" />
              </div>
            </div>
            {form.scope === "team" && (
              <div>
                <label className="overline block mb-1">Equipo *</label>
                <select required data-testid="ann-team" value={form.target_id} onChange={(e) => setForm({ ...form, target_id: e.target.value })} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
                  <option value="">— Selecciona —</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
            {form.scope === "category" && (
              <div>
                <label className="overline block mb-1">Categoría *</label>
                <select required data-testid="ann-cat" value={form.target_id} onChange={(e) => setForm({ ...form, target_id: e.target.value })} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
                  <option value="">— Selecciona —</option>
                  {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}
            {form.scope === "custom" && (
              <div>
                <label className="overline block mb-1">Usuarios destinatarios *</label>
                <div className="border border-border max-h-52 overflow-y-auto p-2 space-y-1" data-testid="ann-users-list">
                  {users.filter(u => u.role !== "super_admin").map(u => (
                    <label key={u.id} className="flex items-center gap-2 text-sm p-2 hover:bg-white/[0.02] cursor-pointer">
                      <input type="checkbox" checked={form.target_ids?.includes(u.id) || false}
                        data-testid={`ann-user-${u.id}`}
                        onChange={(e) => setForm(f => ({
                          ...f,
                          target_ids: e.target.checked
                            ? [...(f.target_ids || []), u.id]
                            : (f.target_ids || []).filter(x => x !== u.id),
                        }))}
                        className="accent-primary" />
                      <span className="font-mono text-xs">{u.name}</span>
                      <span className="text-[10px] text-muted-foreground uppercase ml-auto">{u.role}</span>
                    </label>
                  ))}
                  {users.length === 0 && <div className="text-xs text-muted-foreground p-2">No hay otros usuarios en el club.</div>}
                </div>
                <div className="text-[10px] text-muted-foreground mt-1">{(form.target_ids || []).length} seleccionado(s)</div>
              </div>
            )}
            {form.scope === "players" && (
              <div>
                <label className="overline block mb-1">Jugadores destinatarios *</label>
                <div className="border border-border p-2 space-y-2" data-testid="ann-players-block">
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      data-testid="ann-players-team-filter"
                      value={playerFilterTeam}
                      onChange={(e) => setPlayerFilterTeam(e.target.value)}
                      className="h-8 bg-card border border-border px-2 text-xs font-mono focus:border-primary focus:outline-none"
                    >
                      <option value="">Todos los equipos</option>
                      {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <button
                      type="button"
                      data-testid="ann-players-select-all"
                      onClick={() => {
                        const visible = players.filter(p => !playerFilterTeam || p.team_id === playerFilterTeam);
                        const visibleIds = visible.map(p => p.id);
                        const currentSelected = new Set(form.player_ids || []);
                        const allSelected = visibleIds.every(id => currentSelected.has(id));
                        if (allSelected) {
                          setForm(f => ({ ...f, player_ids: (f.player_ids || []).filter(id => !visibleIds.includes(id)) }));
                        } else {
                          setForm(f => ({ ...f, player_ids: Array.from(new Set([...(f.player_ids || []), ...visibleIds])) }));
                        }
                      }}
                      className="h-8 px-3 border border-border text-[10px] uppercase font-heading tracking-wider hover:border-primary hover:text-primary"
                    >Seleccionar todos</button>
                    <span className="text-[10px] font-mono text-muted-foreground ml-auto">
                      {(form.player_ids || []).length} seleccionado(s)
                    </span>
                  </div>
                  <div className="border border-border max-h-60 overflow-y-auto" data-testid="ann-players-list">
                    {players
                      .filter(p => !playerFilterTeam || p.team_id === playerFilterTeam)
                      .map(p => {
                        const contacts = p.parent_contacts || [];
                        const hasEmail = contacts.some(c => c.email);
                        return (
                          <label key={p.id} className="flex items-center gap-2 text-sm p-2 hover:bg-white/[0.02] cursor-pointer border-b border-border/40 last:border-b-0">
                            <input type="checkbox"
                              data-testid={`ann-player-${p.id}`}
                              checked={form.player_ids?.includes(p.id) || false}
                              onChange={(e) => setForm(f => ({
                                ...f,
                                player_ids: e.target.checked
                                  ? [...(f.player_ids || []), p.id]
                                  : (f.player_ids || []).filter(x => x !== p.id),
                              }))}
                              className="accent-primary" />
                            <div className="flex-1 min-w-0">
                              <div className="font-mono text-xs truncate">
                                {p.name} {p.jersey_number ? <span className="opacity-60">#{p.jersey_number}</span> : null}
                              </div>
                              <div className="text-[10px] text-muted-foreground">
                                {teams.find(t => t.id === p.team_id)?.name || "—"} · {contacts.length} tutor(es)
                                {!hasEmail && contacts.length > 0 && <span className="text-amber-400 ml-1">(sin email)</span>}
                                {contacts.length === 0 && <span className="text-red-400 ml-1">(sin tutores)</span>}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    {players.length === 0 && <div className="text-xs text-muted-foreground p-3">No hay jugadores accesibles.</div>}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    ℹ️ Se enviará por email a los tutores registrados de cada jugador seleccionado.
                  </div>
                </div>
              </div>
            )}
            {form.scope === "staff" && (
              <div className="text-xs text-muted-foreground border border-dashed border-primary/40 bg-primary/5 p-3">
                📢 Se enviará por email a <b>todos</b> los coordinadores, directores deportivos, entrenadores y delegados del club.
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setShow(false)} className="h-10 px-5 border border-border text-sm uppercase font-heading tracking-wider">Cancelar</button>
              <button type="submit" data-testid="submit-ann-btn" className="btn-volt">Publicar</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
